<script setup lang="ts">
import { NuxtLink } from '#components';
import { ContextMenu, ContextMenuTrigger } from '~/components/ui/context-menu';
import { Avatar, AvatarFallback, AvatarImage } from '~/components/ui/avatar';
import EntityArtworkFallback from '@/components/entity/EntityArtworkFallback.vue';
import { cn } from '@/lib/utils';

interface Props {
    href?: string;
    activateOnClick?: boolean;
    title: string;
    subtitle?: string;
    imageUrl?: string;
    imageAlt?: string;
    fallbackText?: string;
    fallbackClass?: string;
    overlayLabel?: string;
    companionImageUrl?: string;
    companionAlt?: string;
    companionFallbackText?: string;
    compact?: boolean;
    surface?: 'default' | 'embedded' | 'list';
    stretchMedia?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    href: '',
    activateOnClick: false,
    subtitle: '',
    imageUrl: '',
    imageAlt: '',
    fallbackText: 'VT',
    fallbackClass: '',
    overlayLabel: 'View',
    companionImageUrl: '',
    companionAlt: '',
    companionFallbackText: '',
    compact: false,
    surface: 'default',
    stretchMedia: false,
});

const emit = defineEmits<{
    activate: [];
}>();

const slots = useSlots();
const hasImage = computed(() => Boolean(String(props.imageUrl || '').trim()));

function resolveInteractiveTag() {
    if (props.href) return NuxtLink;
    if (props.activateOnClick) return 'button';
    return 'div';
}

function resolveInteractiveAttrs() {
    if (props.href) return { to: props.href };
    if (props.activateOnClick) return { type: 'button' as const, onClick: () => emit('activate') };
    return {};
}

const compactRootClass = computed(() => cn(
    'entity-card-shell group w-full max-w-full space-y-2',
    props.surface === 'list'
        ? 'overflow-hidden px-3 py-2 transition-colors duration-200 ease-out hover:bg-muted/45 focus-within:bg-muted/45 motion-reduce:transition-none sm:px-4 sm:py-2.5'
        : props.surface === 'embedded'
            ? 'rounded-xl border border-border/70 bg-card px-4 py-3 shadow-none transition-colors hover:bg-accent/20'
            : 'theme-surface-shadow rounded-xl border bg-card px-4 py-3 text-card-foreground',
));

const fullRootClass = computed(() => cn(
    'entity-card-shell group relative flex w-full max-w-full flex-col gap-2.5 md:flex-row md:items-stretch md:gap-5 md:px-4 md:py-2.5',
    props.surface === 'list'
        ? 'overflow-hidden px-3 py-2 transition-colors duration-200 ease-out hover:bg-muted/45 focus-within:bg-muted/45 motion-reduce:transition-none sm:px-4 sm:py-2.5 md:min-h-[80px]'
        : props.surface === 'embedded'
            ? 'rounded-xl border border-border/70 bg-card shadow-none transition-colors hover:bg-accent/20'
        : 'theme-surface-shadow rounded-xl border bg-card text-card-foreground',
));

const desktopMediaRailClass = computed(() => cn(
    'hidden shrink-0 transition-[width] duration-300 ease-out md:flex',
    props.surface === 'list'
        ? 'w-[88px] md:items-center'
        : (props.stretchMedia || !hasImage.value)
        ? 'w-[96px] self-stretch md:items-stretch'
        : 'w-[96px] md:items-center',
));

const desktopMediaInnerClass = computed(() => cn(
    'flex gap-0',
    props.surface === 'list'
        ? 'h-[80px] items-center py-0 pr-5'
        : (props.stretchMedia || !hasImage.value)
        ? 'h-full items-stretch'
        : 'h-[96px] items-center py-1',
));

const desktopMediaLinkClass = computed(() => cn(
    'relative block shrink-0 cursor-pointer overflow-hidden border border-border/70 bg-muted p-0 text-left transition-[width] duration-300 ease-out',
    props.surface === 'list'
        ? 'h-[80px] w-[80px] rounded-lg'
        : (props.stretchMedia || !hasImage.value)
        ? 'h-full min-h-[96px] w-22'
        : 'h-[96px] w-22 rounded-2xl group-hover:w-30 group-focus-within:w-28',
));
</script>

<template>
    <ContextMenu class="">
        <ContextMenuTrigger v-if="compact" as-child>
            <div :class="compactRootClass">
                <div class="flex items-start gap-3">
                    <div class="min-w-0 flex-1">
                        <NuxtLink
                            v-if="href"
                            :to="href"
                            class="block truncate text-sm font-semibold leading-snug text-foreground underline-offset-2 transition-colors hover:text-[var(--interactive-text)] hover:underline"
                        >
                            {{ title }}
                        </NuxtLink>
                        <p v-else class="truncate text-sm font-semibold leading-snug text-foreground">{{ title }}</p>
                        <p v-if="subtitle" class="mt-0.5 truncate text-xs text-muted-foreground">{{ subtitle }}</p>
                    </div>

                    <slot name="compact-actions" />
                </div>

                <slot name="compact-body" />
            </div>
        </ContextMenuTrigger>

        <ContextMenuTrigger v-else as-child>
            <div :class="fullRootClass">
                <div
                    :class="desktopMediaRailClass"
                >
                    <div :class="desktopMediaInnerClass">
                        <component
                            :is="resolveInteractiveTag()"
                            v-bind="resolveInteractiveAttrs()"
                            :class="desktopMediaLinkClass"
                        >
                            <img
                                v-if="imageUrl"
                                :src="imageUrl"
                                :alt="imageAlt || `${title} preview`"
                                class="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.025] motion-reduce:transition-none"
                            >
                            <div
                                v-else
                                class="h-full w-full"
                            >
                                <EntityArtworkFallback
                                    :seed="title"
                                />
                            </div>
                            <div class="absolute inset-0 bg-black/0 opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:bg-black/15 group-focus-within:opacity-100 group-focus-within:bg-black/15" />
                            <span class="absolute inset-0 flex items-center justify-center text-[11px] font-semibold tracking-wide text-white opacity-0 [text-shadow:0_2px_10px_rgba(15,23,42,0.95)] transition-opacity duration-300 group-hover:opacity-100 group-focus-within:opacity-100">
                                {{ overlayLabel }}
                            </span>
                        </component>

                        <Avatar
                            v-if="companionImageUrl || companionFallbackText"
                            class="-ml-5 h-11 w-11 shrink-0 self-center border-2 border-background shadow-sm"
                        >
                            <AvatarImage :src="companionImageUrl || ''" :alt="companionAlt || title" />
                            <AvatarFallback class="text-xs font-semibold">
                                {{ companionFallbackText }}
                            </AvatarFallback>
                        </Avatar>
                    </div>
                </div>

                <div class="flex shrink-0 gap-3 md:hidden">
                    <component
                        :is="resolveInteractiveTag()"
                        v-bind="resolveInteractiveAttrs()"
                        class="relative h-20 w-20 cursor-pointer overflow-hidden rounded-2xl border border-border/70 bg-transparent p-0 text-left shadow-sm transition-colors duration-200 group-hover:border-border group-focus-within:border-border sm:h-24 sm:w-24"
                    >
                        <img
                            v-if="imageUrl"
                            :src="imageUrl"
                            :alt="imageAlt || title"
                            class="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.025] motion-reduce:transition-none"
                        >
                        <div
                            v-else
                            class="h-full w-full"
                        >
                            <EntityArtworkFallback
                                :seed="title"
                            />
                        </div>
                    </component>

                    <div class="min-w-0 flex-1 space-y-2">
                        <div class="flex min-w-0 flex-wrap items-center gap-2">
                            <component
                                :is="href ? NuxtLink : (activateOnClick ? 'button' : 'p')"
                                v-bind="href ? { to: href } : (activateOnClick ? { type: 'button', onClick: () => emit('activate') } : {})"
                                class="min-w-0 max-w-full truncate bg-transparent p-0 text-left text-base font-semibold tracking-tight text-foreground transition-colors hover:text-[var(--interactive-text)]"
                            >
                                {{ title }}
                            </component>
                            <slot name="badges" />
                        </div>

                        <p v-if="subtitle" class="truncate text-xs text-muted-foreground sm:text-sm">
                            {{ subtitle }}
                        </p>

                        <div v-if="slots['mobile-actions'] || slots.actions" class="flex flex-wrap items-center gap-2 pt-1">
                            <slot v-if="slots['mobile-actions']" name="mobile-actions" />
                            <slot v-else name="actions" />
                        </div>
                    </div>
                </div>

                <div class="min-w-0 flex-1 space-y-3">
                    <div class="hidden min-w-0 md:block">
                        <div class="flex items-start justify-between gap-3">
                            <div class="min-w-0">
                                <div class="flex min-w-0 flex-wrap items-center gap-2">
                                    <component
                                        :is="href ? NuxtLink : (activateOnClick ? 'button' : 'p')"
                                        v-bind="href ? { to: href } : (activateOnClick ? { type: 'button', onClick: () => emit('activate') } : {})"
                                        class="min-w-0 max-w-full truncate bg-transparent p-0 text-left text-base font-semibold tracking-tight text-foreground transition-colors hover:text-[var(--interactive-text)]"
                                    >
                                        {{ title }}
                                    </component>
                                    <slot name="badges" />
                                </div>

                                <p v-if="subtitle" class="mt-1 truncate text-sm text-muted-foreground">
                                    <!-- {{ subtitle }} -->
                                </p>
                            </div>

                            <div v-if="slots.actions" class="flex shrink-0 items-center gap-2">
                                <slot name="actions" />
                            </div>
                        </div>
                    </div>

                    <slot name="body" />
                </div>
            </div>
        </ContextMenuTrigger>

        <slot name="context-menu" />
    </ContextMenu>
</template>
